import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route, RouterProvider, useLocation, useRouteError } from 'react-router';
import { ShellProvider } from '@/context/shell-context';
import { BusyProvider } from '@/context/busy-context';
import { getSessionError, requireSession } from '@/security/session';
import BusyOverlay from '@components/busy-overlay';
import Layout from '@components/layout';
import About from '@views/about';
import Now from '@views/register/now';
import DonorDashboard from '@views/register/donor-dashboard';
import NotFound from '@views/notfound';
import QuickStart from './views/register/screening/quick-start';
import AcceptTransfusions from './views/register/screening/accept-transfusions';
import AssessNutcracker from './views/register/screening/assess-nutcracker';
import AssessLiverHealth from './views/register/screening/assess-liver-health';
import AssessOrganDisease from './views/register/screening/assess-organ-disease';
import AssessHeartAttack from './views/register/screening/assess-heart-attack';
import AssessDiabetes from './views/register/screening/assess-diabetes';
import AssessCancer from './views/register/screening/assess-cancer';
import AssessKidneyStones from './views/register/screening/assess-kidney-stones';
import PregnancyOrNursing from './views/register/screening/pregnancy-or-nursing';
import AssessTobacco from './views/register/screening/assess-tobacco';
import AssessHiv from './views/register/screening/assess-hiv';
import DonationDetails from './views/register/screening/donation-details';
import DonationDetailsMicrosite from './views/register/screening/donation-details-microsite';
import VoucherChoice from './views/register/screening/voucher-choice';
import ScreeningSummary from './views/register/screening/screening-summary';
import ScreeningComplete from './views/register/screening/screening-complete';
import HistoryDemographics from './views/register/history/history-demographics';
import HistoryDiagnosed from './views/register/history/history-diagnosed';
import HistoryFamily from './views/register/history/history-family';
import HistoryMisc from './views/register/history/history-misc';
import HistorySocial from './views/register/history/history-social';
import HistorySummary from './views/register/history/history-summary';
import StartSurvey from './views/register/surveys/start-survey';
import PendingWorkup from './views/register/surveys/pending-workup';
import PendingPreOp from './views/register/surveys/pending-preop';
import PendingSurgery from './views/register/surveys/pending-surgery';
import Survey from './views/register/surveys/survey';
import LabOrders from './views/register/pwl/lab-orders';
import PwlJug from './views/register/pwl/pwl-jug';
import PwlJugMissing from './views/register/pwl/pwl-jug-missing';
import PwlJugStillMissing from './views/register/pwl/pwl-jug-still-missing';
import PwlJugSelf from './views/register/pwl/pwl-jug-self';
import PwlLabQuestComplete from './views/register/pwl/pwl-lab-quest-complete';
import PwlLabWait from './views/register/pwl/pwl-lab-wait';
import OptOut from './views/register/opt-out';
import TriggerOptIn from './views/register/trigger-opt-in';
import NkrCenters from './views/register/nkr-centers';
import UpdateLabs from './views/register/update-labs';
import AuthValidate from './views/security/auth-validate';
import AuthLogin from './views/security/auth-login';
import LoginAs from './views/security/login-as';
import { TestDiagnostic } from './views/register/test-diagnostic';

// Keep the router basename in sync with `base` in vite.config.ts (BASE_URL is '/new-dash/').
const basename = import.meta.env.BASE_URL.replace(/\/$/, '');

const SessionError = () => {
    const error = useRouteError();

    return (
        <div className='container'>
            <div className='alert alert-danger'>{getSessionError(error)}</div>
        </div>
    );
};

const RedirectToNow = () => {
    const { search } = useLocation();

    return <Navigate to={{ pathname: '/register/now', search }} />;
};

const ShellLayout = () => (
    <ShellProvider>
        <BusyProvider>
            <BusyOverlay />
            <Layout />
        </BusyProvider>
    </ShellProvider>
);

const router = createBrowserRouter(
    createRoutesFromElements(
        <Route element={<ShellLayout />}>
            <Route index element={<RedirectToNow />} />
            <Route path='about' element={<About />} />
            <Route path='/register/now' element={<Now fromMicrosite={false} />} />
            <Route path='/register/microsite' element={<Now fromMicrosite={true} />} />
            <Route path='/update-labs' element={<UpdateLabs />} />
            <Route path='/register/auth-login' element={<AuthLogin />} />
            <Route path='/register/auth-validate/:token' element={<AuthValidate />} />
            <Route path='/register/login-as' element={<LoginAs />} />
            <Route id='authenticated' loader={requireSession} shouldRevalidate={() => true} errorElement={<SessionError />} element={<Outlet />}>
                <Route path='/register/donate/:registrationId?/:editKey?' element={<DonorDashboard />} />
                <Route path='/register/screening/quick-start/:registrationId?/:editKey?' element={<QuickStart />} />
                <Route path='/register/screening/accept-transfusions/:registrationId?/:editKey?' element={<AcceptTransfusions />} />
                <Route path='/register/screening/assess-nutcracker/:registrationId?/:editKey?' element={<AssessNutcracker />} />
                <Route path='/register/screening/assess-liver-health/:registrationId?/:editKey?' element={<AssessLiverHealth />} />
                <Route path='/register/screening/assess-organ-disease/:registrationId?/:editKey?' element={<AssessOrganDisease />} />
                <Route path='/register/screening/assess-heart-attack/:registrationId?/:editKey?' element={<AssessHeartAttack />} />
                <Route path='/register/screening/assess-diabetes/:registrationId?/:editKey?' element={<AssessDiabetes />} />
                <Route path='/register/screening/assess-cancer/:registrationId?/:editKey?' element={<AssessCancer />} />
                <Route path='/register/screening/assess-kidney-stones/:registrationId?/:editKey?' element={<AssessKidneyStones />} />
                <Route path='/register/screening/pregnancy-or-nursing/:registrationId?/:editKey?' element={<PregnancyOrNursing />} />
                <Route path='/register/screening/assess-tobacco/:registrationId?/:editKey?' element={<AssessTobacco />} />
                <Route path='/register/screening/assess-hiv/:registrationId?/:editKey?' element={<AssessHiv />} />
                <Route path='/register/screening/donation-details/:registrationId?/:editKey?' element={<DonationDetails />} />
                <Route path='/register/screening/donation-details-microsite/:registrationId?/:editKey?' element={<DonationDetailsMicrosite />} />
                <Route path='/register/screening/voucher-choice/:registrationId?/:editKey?' element={<VoucherChoice />} />
                <Route path='/register/screening/summary/:registrationId?/:editKey?' element={<ScreeningSummary />} />
                <Route path='/register/screening/complete/:registrationId?/:editKey?' element={<ScreeningComplete />} />
                <Route path='/register/history/demographics/:registrationId?/:editKey?' element={<HistoryDemographics />} />
                <Route path='/register/history/diagnosed/:registrationId?/:editKey?' element={<HistoryDiagnosed />} />
                <Route path='/register/history/family/:registrationId?/:editKey?' element={<HistoryFamily />} />
                <Route path='/register/history/misc/:registrationId?/:editKey?' element={<HistoryMisc />} />
                <Route path='/register/history/social/:registrationId?/:editKey?' element={<HistorySocial />} />
                <Route path='/register/history/summary/:registrationId?/:editKey?' element={<HistorySummary />} />
                <Route path='/register/start-survey/:registrationId?/:editKey?' element={<StartSurvey />} />
                <Route path='/register/pending-workup/:registrationId?/:editKey?' element={<PendingWorkup />} />
                <Route path='/register/pending-preop/:registrationId?/:editKey?' element={<PendingPreOp />} />
                <Route path='/register/pending-surgery/:registrationId?/:editKey?' element={<PendingSurgery />} />
                <Route path='/register/survey/:registrationId?/:editKey?' element={<Survey />} />
                <Route path='/register/lab-orders/:registrationId?/:editKey?' element={<LabOrders />} />
                <Route path='/register/pwl-download-scrip/:registrationId?/:editKey?' element={<LabOrders />} />
                <Route path='/register/pwl-jug/:registrationId?/:editKey?' element={<PwlJug />} />
                <Route path='/register/pwl-jug-received/:registrationId?/:editKey?' element={<LabOrders />} />
                <Route path='/register/pwl-jug-missing/:registrationId?/:editKey?' element={<PwlJugMissing />} />
                <Route path='/register/pwl-jug-still-missing/:registrationId?/:editKey?' element={<PwlJugStillMissing />} />
                <Route path='/register/pwl-jug-self/:registrationId?/:editKey?' element={<PwlJugSelf />} />
                <Route path='/register/pwl-next-steps/:registrationId?/:editKey?' element={<LabOrders />} />
                <Route path='/register/pwl-lab-start/:registrationId?/:editKey?' element={<LabOrders />} />
                <Route path='/register/pwl-lab-wait/:registrationId?/:editKey?' element={<PwlLabWait />} />
                <Route path='/register/pwl-lab-quest-complete/:registrationId?/:editKey?' element={<PwlLabQuestComplete />} />
                <Route path='/register/opt-out/:registrationId?/:editKey?' element={<OptOut />} />
                <Route path='/register/trigger-opt-in/:registrationId?/:editKey?' element={<TriggerOptIn />} />
                <Route path='/register/select-center/:registrationId?/:editKey?' element={<NkrCenters />} />
                <Route path='/register/pwl-lab-choose-center/:registrationId?/:editKey?' element={<NkrCenters />} />
                <Route path='/register/continue/:registrationId?/:editKey?' element={<DonorDashboard />} />
                <Route path='/register/test-diagnostic' element={<TestDiagnostic />} />
            </Route>
            <Route path='*' element={<NotFound />} />
        </Route>
    ),
    { basename },
);

const App = () => <RouterProvider router={router} />;

export default App;
